import {Box, Grid, Typography} from '@mui/material';
import {notEmpty} from 'main/utility/CommonUtil';
import {getFormattedValue} from 'main/utility/helper/FormatUtil';
import {getObjectValue} from 'main/utility/LayoutUtil';
import PropTypes from 'prop-types';

const getBilling = (widgetData, bill) => {
  return (
    <Grid item xs={12}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: 'primary.main',
          mb: 2,
          textTransform: 'uppercase',
        }}
      >
        {bill.label}
      </Typography>
      <Typography sx={{fontWeight: 700, mb: 1}}>
        {getObjectValue(widgetData, bill.name)}
      </Typography>
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: 'pre-line',
        }}
      >
        {getObjectValue(widgetData, bill.address)}
      </Typography>
    </Grid>
  );
};

const getInfo = (widgetData, field) => {
  return (
    <Box component='tr' key={field.name}>
      <Box
        component='td'
        sx={{
          pr: 2,
          fontSize: 11,
          fontWeight: 700,
          color: 'text.disabled',
          textTransform: 'uppercase',
          letterSpacing: '0.02rem',
          verticalAlign: 'middle',
        }}
      >
        {field.label}:
      </Box>
      <Box
        component='td'
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: 'text.primary',
          whiteSpace: 'nowrap', // Keeps dates on one line
          verticalAlign: 'middle',
        }}
      >
        {getFormattedValue(field, getObjectValue(widgetData, field.name))}
      </Box>
    </Box>
  );
};

const DocHeader = ({widgetSpec, widgetData}) => {
  const {widgettitle, header} = widgetSpec;
  const {
    invoiceid = {},
    company = {},
    client = {},
    invoicetotal = {},
    other = [],
  } = header;
  const logo = getObjectValue(widgetData, company.logo) || 'YS-avatar.png';

  return (
    <Box sx={{mb: 8}}>
      {/* Letterhead */}
      <Box
        sx={{
          bgcolor: 'secondary.main',
          color: 'common.white',
          p: 6,
          mx: (theme) => `-${theme.spacing(8)}`, // Flush to card edges
          mt: (theme) => `-${theme.spacing(8)}`,
          mb: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: (theme) =>
            `${theme.sharpRadiu}px ${theme.sharpRadiu}px 0 0`,
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 4}}>
          <Box
            component='img'
            src={`/assets/images/${logo}`}
            sx={{height: 40, filter: 'brightness(0) invert(1)'}}
          />
          <Typography
            variant='h1'
            sx={{
              color: 'white',
              m: 0,
              fontSize: 22,
              letterSpacing: '2px',
              textTransform: 'uppercase',
            }}
          >
            {widgettitle}
          </Typography>
        </Box>

        {notEmpty(invoiceid) && (
          <Box sx={{textAlign: 'right'}}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: 'primary.main',
                textTransform: 'uppercase',
              }}
            >
              {invoiceid.label}
            </Typography>
            <Typography sx={{fontWeight: 700, fontSize: 16}}>
              {widgetData[invoiceid.name]}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Info Grid */}
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 6}}>
            {notEmpty(company) && getBilling(widgetData, company)}
            {notEmpty(client) && getBilling(widgetData, client)}
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            ml: 'auto',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-start', // Forces the top of this block to match "Bill To"
          }}
        >
          <Box
            component='table'
            sx={{
              width: 'auto', // Hugs the content
              ml: 'auto', // Pins the entire table to the right
            }}
          >
            <tbody>
              {notEmpty(other) &&
                other.map((field) => getInfo(widgetData, field))}

              {/* INVOICE TOTAL ROW */}
              {notEmpty(invoicetotal) && (
                <Box component='tr'>
                  <Box
                    component='td'
                    sx={{
                      pr: 2, // Space between label and value
                      fontSize: 13,
                      fontWeight: 800,
                      color: 'text.primary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02rem',
                      verticalAlign: 'middle',
                      pt: 4,
                    }}
                  >
                    {invoicetotal.label}:
                  </Box>
                  <Box
                    component='td'
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 800,
                      color: 'primary.main', // Professional Gold
                      whiteSpace: 'nowrap', // Keeps dates on one line
                      verticalAlign: 'middle',
                      pt: 4,
                    }}
                  >
                    {getFormattedValue(
                      invoicetotal,
                      getObjectValue(widgetData, invoicetotal.name),
                    )}
                  </Box>
                </Box>
              )}
            </tbody>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

DocHeader.propTypes = {
  widgetSpec: PropTypes.object,
  widgetData: PropTypes.object,
};

export default DocHeader;
